<template>
  <div class="fixed inset-0 flex items-center justify-center z-50">
    <!-- Background Image with multiply blend effect -->
    <div class="fixed inset-0 -z-30">
      <img src="/assets/photo.jpg" alt="Background" class="w-full h-full object-cover opacity-50 bg-image-animate" />
    </div>
    
    <!-- Primary multiply blend overlay for rich color interaction -->
    <div class="fixed inset-0 -z-20 bg-gradient-to-br from-blue-900/70 via-blue-900/70 to-gray-900/70 mix-blend-multiply"></div>
    
    <!-- Secondary overlay for better text readability -->
    <div class="fixed inset-0 -z-10 bg-black/40 backdrop-blur-[2px]"></div>
    
    <!-- Additional dark base layer -->
    <div class="fixed inset-0 bg-gray-900" style="z-index: -40;"></div>

    <div
      class="relative z-10 mt-7 bg-white border border-gray-200 rounded-xl shadow-sm dark:bg-gray-800 dark:border-gray-700">
      <div class="p-4 sm:p-7">
        <div class="text-center">
          <router-link to="/" aria-label="Back to Engagr home" class="block">
            <img class="m-auto w-24 z-10 pb-4 text-blue-100n" :src="logoURLBLK"/>
          </router-link>
          <h1 class="block text-1xl lg:text-2xl font-bold text-gray-800 dark:text-white">Sign in to your account</h1>
        </div>

        <div class="mt-5">
          <!-- Form -->
          <form @submit.prevent="login">
            <div class="grid gap-y-4">
              <!-- Form Group -->
              <div>
                <div class="relative">
                  <input v-model="password" :type="passwordFieldType" id="text" name="text"
                    class="py-3 px-4 block w-full border-gray-200 rounded-md text-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
                    placeholder="Enter password">
                  <div class="absolute inset-y-0 right-0 pr-3 h-12 flex items-center cursor-pointer"
                    @click="togglePasswordView">

                    <div v-if="passwordFieldType === 'password'">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round"
                          d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>

                    </div>

                    <!-- Eye Off Icon when password is visible -->
                    <div v-else>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="currentColor" class="w-6 h-6">
                        <path stroke-linecap="round" stroke-linejoin="round"
                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                  </div>
                  <div v-if="error" class="text-red-500 mt-2">{{ error }}</div>

                  <div class="hidden absolute inset-y-0 right-0 flex items-center pointer-events-none pr-3">
                    <svg class="h-5 w-5 text-red-500" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"
                      aria-hidden="true">
                      <path
                        d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
                    </svg>
                  </div>
                </div>
              </div>
              <!-- End Form Group -->

              <button :disabled="!password" :class="{
                'py-3 px-4 inline-flex justify-center items-center gap-2 rounded-md border border-transparent font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all text-sm': true,
                'bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-500 dark:focus:ring-offset-gray-800': password,
                'bg-gray-500 text-white cursor-not-allowed': !password
              }" type="submit">
                Login
              </button>

            </div>
          </form>
          <!-- End Form -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LoginScreen',
  props: ['post'],
  created() {
    // Load the password from localStorage when the component is created
    this.password = localStorage.getItem('rememberedPassword') || '';
  },
  methods: {
    async login() {
      try {
        await this.$store.dispatch("login", { post_id: this.post, password: this.password });
        this.error = this.$store.state.loginError;
        // Always store the password in localStorage
        localStorage.setItem('rememberedPassword', this.password);
      } catch (error) {
        console.error(error);
      }
    },
    togglePasswordView() {
      this.passwordFieldType = this.passwordFieldType === 'password' ? 'text' : 'password';
    },
  },
  data() {
    return {
      post_id: "",
      password: "",
      error: null,
      passwordFieldType: 'password',
      logoURLBLK: '/assets/engagr-blk.svg'
    };
  },
};
</script>

<style scoped>
.bg-image-animate {
  animation: subtle-zoom 30s ease-in-out infinite;
}

@keyframes subtle-zoom {
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
}
</style>
